import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import Divider from "../../../../components/atoms/Divider.atom"
import GridContainer from "../../../../components/layouts/containers/GridContainer.container"
import GridItem from "../../../../components/layouts/containers/GridItem.container"
import { ValueWithLabelTemplate } from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import {
  getVacationRangeLabel,
  isForeignSchema,
} from "../../../../utils/vacation.utils"
import VacationFlag from "../../../../components/molecules/VacationFlag.molecule"
import { DateTime } from "luxon"
import VacationDayTypesBreakdown from "../../components/VacationDayTypesBreakdown.molecule"
import PrimaryButton from "../../../../components/molecules/buttons/PrimaryButton.molecule"
import DangerButton from "../../../../components/molecules/buttons/DangerButton.molecule"
import {
  approveChangeVacationRequest,
  rejectChangeVacationRequest,
  getLastApprovedVacationRangeByVacationId,
} from "../../../../services/vacation.service"
import useToast from "../../../../hooks/useToast.hook"
import ProgressSpinner from "../../../../components/atoms/misc/ProgressSpinner.atom"
import {
  VACATIONS_APPROVE_CHANGE_VACATION_REQUEST_SUCCESS,
  VACATIONS_REJECT_CHANGE_VACATION_REQUEST_SUCCESS,
  VACATIONS_SAVE_ERROR_MESSAGE,
} from "../../../../consts/modules/vacations.consts"
import { useSession } from "../../../../hooks/auth/useSession.hook"

const ModalFormChangeRequestReview = ({
  visible,
  setVisible,
  selectedVacationRequest,
  triggerRefresh,
}) => {
  const { user } = useSession()
  const thisVacationWasCreatedByMe =
    selectedVacationRequest?.createdBy === user?.id
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState({
    approve: false,
    reject: false,
  })
  const [lastApprovedVacationRequest, setLastApprovedVacationRequest] =
    useState()
  const { setSuccessMessage, setErrorMessage } = useToast()

  useEffect(() => {
    if (visible && selectedVacationRequest) {
      fetchLastApprovedVacationRequest()
    }
  }, [selectedVacationRequest])
  const fetchLastApprovedVacationRequest = async () => {
    setLoading(true)
    try {
      const response = await getLastApprovedVacationRangeByVacationId(
        selectedVacationRequest.idVacations
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      setLastApprovedVacationRequest(response.result[0])
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }
  const setApproving = (value) => {
    setSubmitting((prev) => ({
      ...prev,
      approve: value,
    }))
  }
  const setRejecting = (value) => {
    setSubmitting((prev) => ({
      ...prev,
      reject: value,
    }))
  }
  const handleRejectSubmit = async () => {
    setRejecting(true)
    try {
      await rejectChangeVacationRequest(selectedVacationRequest.idVacations)
      triggerRefresh()
      setSuccessMessage(VACATIONS_REJECT_CHANGE_VACATION_REQUEST_SUCCESS)
    } catch (error) {
      setErrorMessage(VACATIONS_SAVE_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
      setVisible(false)
    }
  }
  const handleApproveSubmit = async () => {
    setApproving(true)
    try {
      await approveChangeVacationRequest(selectedVacationRequest.idVacations)
      triggerRefresh()
      setSuccessMessage(VACATIONS_APPROVE_CHANGE_VACATION_REQUEST_SUCCESS)
    } catch (error) {
      setErrorMessage(VACATIONS_SAVE_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
      setVisible(false)
    }
  }
  const modalFooterTemplate = () => {
    return (
      <div>
        <DangerButton
          label="Rechazar"
          btnType="outlined"
          icon={`pi ${submitting.reject ? "pi-spin pi-spinner" : "pi-times"}`}
          onClick={handleRejectSubmit}
          disabled={submitting.reject || loading}
          rounded
        />
        <PrimaryButton
          label="Aprobar"
          icon={`pi ${submitting.approve ? "pi-spin pi-spinner" : "pi-check"}`}
          onClick={handleApproveSubmit}
          disabled={submitting.approve || loading}
          rounded
        />
      </div>
    )
  }

  if (!selectedVacationRequest) {
    return null
  }
  return (
    <>
      <ModalForm
        className={`vacation-change-review-modal-form ${thisVacationWasCreatedByMe && user.isAdmin() ? "hide-actions-footer" : ""}`}
        headerTitle="Solicitud de cambio"
        headerIconClass="pi pi-refresh"
        footer={modalFooterTemplate()}
        visible={visible}
        setVisible={setVisible}
        dismissableMask
        closable
      >
        <GridContainer className="vacation-request-summary">
          <GridItem col={6}>
            <ValueWithLabelTemplate
              label="Integrante"
              value={
                <div className="flex gap-2 align-items-center">
                  <span>{selectedVacationRequest.teamMember.fullName}</span>
                  <VacationFlag
                    schemaTitle={
                      selectedVacationRequest.teamMember.holidaySchemaName
                    }
                  />
                </div>
              }
            />
          </GridItem>
          <GridItem col={6}>
            <ValueWithLabelTemplate
              label="Fecha de la solicitud"
              value={DateTime.fromISO(
                selectedVacationRequest.createdAt
              ).toFormat("dd/MM/yyyy")}
            />
          </GridItem>
          <GridItem col={6}>
            <ValueWithLabelTemplate
              label="Razón del cambio"
              value={selectedVacationRequest.changeMotive}
            />
          </GridItem>
        </GridContainer>
        <Divider stroke="sm" />
        <div className="max-w-full flex">
          <VacationVersionDetails
            title="Antes"
            vacationRequest={lastApprovedVacationRequest}
            loading={loading}
          />
          <Divider className="mx-3" stroke="sm" vertical />
          <VacationVersionDetails
            title="Ahora"
            vacationRequest={selectedVacationRequest}
          />
        </div>
      </ModalForm>
    </>
  )
}

const VacationVersionDetails = ({ title, vacationRequest, loading }) => {
  return (
    <GridContainer className="vacation-version-details flex-1">
      {title && (
        <GridItem col={12}>
          <span className="value font-cairo font-semibold text-xl">
            {title}
          </span>
        </GridItem>
      )}
      <GridItem col={12}>
        <ValueWithLabelTemplate
          label="Fechas solicitadas"
          value={getVacationRangeLabel(
            vacationRequest?.numberOfVacationDays,
            vacationRequest?.startDate,
            vacationRequest?.endDate,
            {
              includeNumberOfDays: false,
              includeDateRange: true,
              dateFormat: "dd/MM/yyyy",
            }
          )}
          loading={loading}
        />
      </GridItem>
      <GridItem col={12}>
        <ValueWithLabelTemplate
          label="Cantidad de días"
          value={
            <div className="flex gap-2">
              {vacationRequest?.numberOfVacationDays}
              <VacationDayTypesBreakdown vacationRequest={vacationRequest} />
            </div>
          }
          loading={loading}
        />
      </GridItem>
      <GridItem col={12}>
        <ValueWithLabelTemplate
          label="Motivo"
          value={vacationRequest?.vacationsMotive}
          loading={loading}
        />
      </GridItem>
    </GridContainer>
  )
}

export default ModalFormChangeRequestReview

ModalFormChangeRequestReview.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  selectedVacationRequest: PropTypes.object,
}
